
import React from 'react';
import type { Notification } from '../../types';
import { Icons } from '../ui/Icons';
import { safeFormat } from '../../utils/safeDateFns';

interface NotificationItemProps {
    notification: Notification;
    onMarkAsRead: (id: string) => void;
}

const levelConfig = {
    info: { icon: <Icons.HelpCircle className="h-5 w-5 text-blue-500" />, color: 'bg-blue-50 dark:bg-blue-900/20' },
    success: { icon: <Icons.CalendarCheck className="h-5 w-5 text-green-500" />, color: 'bg-green-50 dark:bg-green-900/20' },
    warning: { icon: <Icons.AlertTriangle className="h-5 w-5 text-yellow-500" />, color: 'bg-yellow-50 dark:bg-yellow-900/20' },
    error: { icon: <Icons.AlertTriangle className="h-5 w-5 text-red-500" />, color: 'bg-red-50 dark:bg-red-900/20' },
};

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onMarkAsRead }) => {
    // Defensive rendering: Ensure all rendered values are strings with fallbacks
    // to prevent React Minified Error #31.
    const title = typeof notification.title === 'string' ? notification.title : 'Notification';
    const message = typeof notification.message === 'string' ? notification.message : 'No details available.';
    
    // Use safeFormat for consistent and robust date formatting
    const formattedDate = safeFormat(notification.scheduledAt, 'MMM d, yyyy h:mm a');
    
    const config = levelConfig[notification.level] || levelConfig.info;

    return (
        <li 
            className={`relative p-4 rounded-xl border transition-all duration-200 ${
                !notification.read 
                    ? `border-indigo-100 dark:border-indigo-900/30 ${config.color} shadow-sm` 
                    : 'border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-800/40 opacity-75'
            } hover:shadow-md group`}
        >
            <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 p-2 rounded-lg ${!notification.read ? 'bg-white dark:bg-slate-800 shadow-sm' : 'bg-gray-50 dark:bg-slate-900'}`}>
                    {config.icon}
                </div>
                <div className="flex-grow min-w-0">
                    <div className="flex justify-between items-start gap-2">
                        <p className={`text-sm font-bold truncate ${!notification.read ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                            {title}
                        </p>
                        {!notification.read && (
                            <span className="flex-shrink-0 w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></span>
                        )}
                    </div>
                    <p className={`text-sm mt-1 leading-relaxed ${!notification.read ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}`}>
                        {message}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                        <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                            {formattedDate}
                        </p>
                        {!notification.read && (
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMarkAsRead(notification.id);
                                }} 
                                className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold rounded-md transition-all shadow-sm active:scale-95"
                            >
                                <Icons.Check className="w-3 h-3" />
                                Mark as Read
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </li>
    );
};

export default NotificationItem;